/**
 * The game page's insight column (docs/contest-ux.md, Revision 1), ≥1280px only: a sticky right
 * column for the market last tapped or hovered (default: the moneyline). Top to bottom:
 *
 *   1. the market name (a link to its Market page) and the side in focus
 *   2. every venue's price for every side (public data, so it's useful signed out / on Free);
 *      tap any price to trade there
 *   3. Book: the depth ladder (gated by depth:read)
 *   4. Chart: price history (gated by pricing:history)
 *
 * The page hides it while the ticket drawer is open (the drawer takes its place).
 */
import type { ReactNode } from "react";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import { sideColumnLabel, type PositionLike } from "../../lib/books.js";
import { venueMatrix } from "../../lib/contestMarkets.js";
import { fmtPrice, fmtSize } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { SCOPES, useAccess } from "../../lib/omauth.js";
import { DepthBook } from "../DepthBook.js";
import { PriceHistoryPanel } from "../PriceHistoryPanel.js";
import { FlashText, SoftCard } from "../kit.js";
import { VenueLogo } from "../VenueLogo.js";
import { GateNote, MarketLink, type CardCtx } from "./MarketCards.js";

/** Column label for a side: Over/Under/Yes/No inside one participant's market, else the name (+ line). */
export function insightSideLabel(p: PositionLike, all: PositionLike[]): string {
  const s = (p.side_key ?? "").toLowerCase();
  const oneParticipant = all.every((x) => (x.participant_id ?? "") === (p.participant_id ?? ""));
  if (s === "yes" && !oneParticipant) return p.participant_name ?? p.title ?? "Yes";
  return sideColumnLabel(p, s);
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <SoftCard className="overflow-hidden">
      <div className="border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-dim">{title}</div>
      {children}
    </SoftCard>
  );
}

export function InsightColumn({
  title,
  sides,
  focus,
  ctx,
  onFocus,
}: {
  title: string;
  /** The market's sides, in display order. */
  sides: PositionLike[];
  /** position_hash of the side in focus. */
  focus: string;
  ctx: CardCtx;
  onFocus: (hash: string) => void;
}) {
  const { format } = useOddsFormat();
  const depthOk = useAccess(SCOPES.depth).ok;
  const chartOk = useAccess(SCOPES.pricing).ok;
  const fi = Math.max(0, sides.findIndex((s) => s.position_hash === focus));
  const { rows, best } = venueMatrix(sides, fi);
  const labels = sides.map((s) => insightSideLabel(s, sides));
  const grid = sides.length >= 3 ? "grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))]" : sides.length === 2 ? "grid-cols-[minmax(0,1.4fr)_repeat(2,minmax(0,1fr))]" : "grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]";

  return (
    <div className="flex flex-col gap-3" aria-label="Market insight">
      <div className="min-w-0 px-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-faint">Selected market</div>
        <h2 className="break-words text-[15px] font-semibold leading-snug text-ink">
          <MarketLink href={ctx.marketLink(sides)}>{title}</MarketLink>
        </h2>
        {sides.length > 0 && (
          <div className="truncate text-[12px] text-dim">
            Focus: <span className="text-amber">{labels[fi]}</span>
          </div>
        )}
      </div>

      <Section title={`Every venue · ${rows.length}`}>
        {rows.length === 0 ? (
          <div className="px-3 py-3 text-[12px] text-faint">No venue has a fillable price right now.</div>
        ) : (
          <div className="flex flex-col p-1" role="table" aria-label="Every venue's price">
            <div className={`grid items-end gap-1 px-2 pb-1 pt-1 ${grid}`} role="row">
              <span role="columnheader" className="text-[11px] text-faint">
                Venue
              </span>
              {labels.map((l, i) => (
                <button
                  key={sides[i].position_hash}
                  type="button"
                  role="columnheader"
                  onClick={() => onFocus(sides[i].position_hash)}
                  title={`Sort by ${l}`}
                  className={`min-w-0 truncate text-right text-[11px] font-medium hover:text-ink ${i === fi ? "text-amber" : "text-dim"}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {rows.map((r) => {
              const tradeable = ctx.realVenues ? ctx.realVenues.has(r.partnerId) : true;
              return (
                <div key={r.partnerId} className={`grid items-center gap-1 rounded px-2 ${grid}`} role="row">
                  <span role="rowheader" className="flex min-w-0 items-center gap-2 py-1 text-[13px]">
                    <VenueLogo name={r.name} size={18} />
                    <span className={`min-w-0 truncate ${tradeable ? "text-ink" : "text-dim"}`} title={tradeable ? r.name : `${r.name} · not connected`}>
                      {r.name}
                    </span>
                  </span>
                  {r.cells.map((pl, i) => {
                    const pos = sides[i];
                    if (!pl) {
                      return (
                        <span key={i} role="cell" className="text-right text-[12px] text-faint">
                          —
                        </span>
                      );
                    }
                    const isBest = best[i] != null && pl.price === best[i];
                    const active = ctx.active?.positionHash === pos.position_hash && ctx.active.partnerId === r.partnerId;
                    return (
                      <button
                        key={i}
                        type="button"
                        role="cell"
                        onClick={() => ctx.onPick(pos as LiquidityPosition, pl as PartnerLiquidity)}
                        title={`Buy ${labels[i]} at ${r.name} · ${fmtSize(pl.available)} available`}
                        className={`flex min-h-9 min-w-0 flex-col items-end justify-center rounded px-1 text-right hover:bg-raised ${active ? "bg-amber/10" : ""}`}
                      >
                        <span className={`nums text-[13px] font-semibold ${active ? "text-amber" : isBest ? "text-up" : "text-ink"}`}>
                          <FlashText value={pl.price}>{fmtPrice(pl.price, pl.american_odds, format)}</FlashText>
                        </span>
                        <span className="nums text-[10px] text-faint">{fmtSize(pl.available)}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
            <div className="px-2 pb-1 pt-1.5 text-[11px] text-faint">
              Best price per side in <span className="text-up">green</span>. Tap any price to trade there.
            </div>
          </div>
        )}
      </Section>

      <Section title="Book">
        {depthOk ? <DepthBook positions={sides as LiquidityPosition[]} /> : <GateNote scope={SCOPES.depth} />}
      </Section>

      <Section title="Chart">
        {chartOk ? (
          <PriceHistoryPanel contestId={ctx.contestId} positions={sides as LiquidityPosition[]} flush />
        ) : (
          <GateNote scope={SCOPES.pricing} />
        )}
      </Section>
    </div>
  );
}
